import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { 
  Loader2, AlertCircle, Mail, Lock, User, Phone, ShieldCheck, Upload, 
  UserCheck, Baby, Gift, Heart, Droplets, AlertTriangle, Camera, Shield, CheckCircle
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_findmed-app/artifacts/ge9othd8_LOGO%20SOS%20FINDMED.png";
const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export default function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  const fileInputRef = useRef(null);

  const refCode = searchParams.get('ref') || '';

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    beneficiary_type: 'self',
    beneficiary_name: '',
    guardian_name: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    blood_type: '',
    allergies: '',
    referred_by: refCode
  });
  const [ineFile, setIneFile] = useState(null);
  const [inePreview, setInePreview] = useState(null);
  const [autoRenew, setAutoRenew] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [referrerName, setReferrerName] = useState('');

  useEffect(() => {
    if (refCode) {
      axios.get(`${API_URL}/api/referral/validate/${refCode}`).then(res => {
        if (res.data.valid) setReferrerName(res.data.referrer_name);
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refCode]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const formatPhone = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length <= 2) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 2)} ${digits.slice(2)}`;
    return `${digits.slice(0, 2)} ${digits.slice(2, 6)} ${digits.slice(6)}`;
  };

  const handlePhoneChange = (e) => {
    setFormData({ ...formData, [e.target.name]: formatPhone(e.target.value) });
  };

  const handleIneChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setError('El archivo de INE no debe superar 10MB');
        return;
      }
      setIneFile(file);
      const reader = new FileReader();
      reader.onload = (ev) => setInePreview(ev.target.result);
      reader.readAsDataURL(file);
      setError('');
    }
  };

  const validateStep1 = () => {
    setError('');
    if (!formData.name.trim()) { setError('El nombre completo es obligatorio'); return false; }
    if (!formData.email.trim()) { setError('El correo electrónico es obligatorio'); return false; }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) { setError('Ingresa un correo electrónico válido'); return false; }
    const phoneDigits = formData.phone.replace(/\D/g, '');
    if (!phoneDigits || phoneDigits.length < 10) { setError('Ingresa un número de teléfono válido a 10 dígitos'); return false; }
    if (formData.password.length < 6) { setError('La contraseña debe tener al menos 6 caracteres'); return false; }
    if (formData.password !== formData.confirmPassword) { setError('Las contraseñas no coinciden'); return false; }
    if (formData.beneficiary_type === 'minor' && !formData.beneficiary_name.trim()) { setError('El nombre del menor es obligatorio'); return false; }
    if (formData.beneficiary_type === 'gift' && !formData.beneficiary_name.trim()) { setError('El nombre del beneficiario es obligatorio'); return false; }
    return true;
  };

  const validateStep2 = () => {
    setError('');
    // INE is optional — user can upload later from dashboard
    return true;
  };

  const handleSubmit = async () => {
    if (!acceptTerms || !acceptPrivacy) {
      setError('Debes aceptar los Términos y Condiciones y el Aviso de Privacidad');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const phoneClean = formData.phone.replace(/\D/g, '');
      const result = await register(
        formData.email,
        formData.password,
        formData.name,
        phoneClean,
        {
          beneficiary_type: formData.beneficiary_type,
          beneficiary_name: formData.beneficiary_name || null,
          guardian_name: formData.beneficiary_type === 'minor' ? formData.name : null,
          emergency_contact_name: formData.emergency_contact_name || null,
          emergency_contact_phone: formData.emergency_contact_phone ? formData.emergency_contact_phone.replace(/\D/g, '') : null,
          blood_type: formData.blood_type || null,
          allergies: formData.allergies || null,
          referred_by: formData.referred_by || null
        }
      );

      if (!result.success) {
        setError(result.error);
        setLoading(false);
        return;
      }

      // Upload INE
      if (ineFile) {
        try {
          const ineFormData = new FormData();
          ineFormData.append('ine_file', ineFile);
          await axios.post(`${API_URL}/api/user/upload-ine`, ineFormData, {
            withCredentials: true,
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        } catch (ineErr) {
          console.error('INE upload error:', ineErr);
          // Continue to payment even if INE upload fails - they can upload later
        }
      }

      // Redirect to Stripe
      try {
        const checkoutResponse = await axios.post(`${API_URL}/api/payment/create-checkout`, {
          origin_url: window.location.origin,
          auto_renew: autoRenew
        }, { withCredentials: true });

        if (checkoutResponse.data.url) {
          window.location.href = checkoutResponse.data.url;
          return;
        }
      } catch (paymentErr) {
        console.error('Payment redirect error:', paymentErr);
        // If Stripe fails, go to dashboard where they can retry
        navigate('/dashboard');
      }
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.response?.data?.detail || 'Ocurrió un error. Por favor intenta de nuevo.');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6] px-4 py-12 pt-24">
      <Card className="w-full max-w-lg border-0 shadow-xl" data-testid="register-card">
        <CardHeader className="text-center pb-2">
          <img src={LOGO_URL} alt="SOS FindMed" className="w-48 h-auto mx-auto mb-4" style={{ filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.15))' }} />
          <CardTitle className="text-2xl font-bold text-[#1A3636]">Crear Cuenta y Comprar</CardTitle>
          <CardDescription className="text-[#5C6666]">
            Regístrate y adquiere tu membresía de protección
          </CardDescription>
          {/* Step indicator */}
          <div className="flex justify-center gap-2 mt-4">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center gap-1">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                  step === s ? 'bg-[#C81D25] text-white scale-110' : step > s ? 'bg-emerald-500 text-white' : 'bg-gray-200 text-gray-500'
                }`}>{step > s ? '✓' : s}</div>
                {s < 3 && <div className={`w-8 h-0.5 ${step > s ? 'bg-emerald-500' : 'bg-gray-200'}`} />}
              </div>
            ))}
          </div>
          <div className="text-xs text-gray-500 mt-2">
            {step === 1 && 'Datos personales'}
            {step === 2 && 'Verificación de identidad'}
            {step === 3 && 'Confirmar y pagar'}
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          {error && (
            <Alert variant="destructive" className="mb-4" data-testid="register-error">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* ===== STEP 1: Datos personales ===== */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>¿Para quién es la membresía?</Label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: 'self', label: 'Para mí', icon: UserCheck },
                    { value: 'minor', label: 'Menor de edad', icon: Baby },
                    { value: 'gift', label: 'Regalo/Tercero', icon: Gift }
                  ].map(({ value, label, icon: Icon }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setFormData({ ...formData, beneficiary_type: value })}
                      className={`p-3 rounded-lg border-2 text-center transition-all ${
                        formData.beneficiary_type === value
                          ? 'border-[#C81D25] bg-red-50 text-[#C81D25]'
                          : 'border-gray-200 hover:border-gray-300 text-gray-500'
                      }`}
                      data-testid={`beneficiary-type-${value}`}
                    >
                      <Icon className="w-5 h-5 mx-auto mb-1" />
                      <span className="text-xs font-medium">{label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2"><User className="w-4 h-4" />
                  {formData.beneficiary_type === 'self' ? 'Nombre Completo (como aparece en tu INE)' :
                   formData.beneficiary_type === 'minor' ? 'Nombre del Padre/Madre/Tutor (como aparece en su INE)' :
                   'Tu Nombre Completo (comprador)'}
                  <span className="text-[#C81D25] text-xs">*</span>
                </Label>
                <Input name="name" value={formData.name} onChange={handleChange} placeholder="Nombre completo" required data-testid="register-name-input" />
              </div>

              {formData.beneficiary_type !== 'self' && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    {formData.beneficiary_type === 'minor' ? <Baby className="w-4 h-4" /> : <Gift className="w-4 h-4" />}
                    {formData.beneficiary_type === 'minor' ? 'Nombre Completo del Menor' : 'Nombre del Beneficiario'}
                    <span className="text-[#C81D25] text-xs">*</span>
                  </Label>
                  <Input name="beneficiary_name" value={formData.beneficiary_name} onChange={handleChange} 
                    placeholder={formData.beneficiary_type === 'minor' ? 'Nombre del menor de edad' : 'Nombre de quien recibirá la membresía'} 
                    required data-testid="register-beneficiary-name" />
                </div>
              )}

              <div className="space-y-2">
                <Label className="flex items-center gap-2"><Mail className="w-4 h-4" />Correo Electrónico <span className="text-[#C81D25] text-xs">*</span></Label>
                <Input name="email" type="email" value={formData.email} onChange={handleChange} placeholder="tu@email.com" required data-testid="register-email-input" />
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2"><Phone className="w-4 h-4" />Teléfono Celular <span className="text-[#C81D25] text-xs">*</span></Label>
                <Input name="phone" value={formData.phone} onChange={handlePhoneChange} placeholder="55 1234 5678" required data-testid="register-phone-input" />
                <p className="text-xs text-gray-400">A este número nos comunicaremos en caso de emergencia</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Lock className="w-4 h-4" />Contraseña <span className="text-[#C81D25] text-xs">*</span></Label>
                  <Input name="password" type="password" value={formData.password} onChange={handleChange} placeholder="Mín. 6 caracteres" required data-testid="register-password-input" />
                </div>
                <div className="space-y-2">
                  <Label>Confirmar <span className="text-[#C81D25] text-xs">*</span></Label>
                  <Input name="confirmPassword" type="password" value={formData.confirmPassword} onChange={handleChange} placeholder="Repetir" required data-testid="register-confirm-password-input" />
                </div>
              </div>

              {/* Referral code */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-sm text-gray-500">
                  <Gift className="w-4 h-4" /> Código de referido <span className="text-xs font-normal text-gray-400">(opcional)</span>
                </Label>
                <Input 
                  name="referred_by" 
                  value={formData.referred_by} 
                  onChange={(e) => setFormData({...formData, referred_by: e.target.value.toUpperCase()})}
                  placeholder="Ej: REF-CARLOS-A1B2" 
                  data-testid="referral-code-input"
                  className={referrerName ? 'border-emerald-400 bg-emerald-50' : ''}
                />
                {referrerName && (
                  <p className="text-xs text-emerald-600 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" /> Referido por: <strong>{referrerName}</strong>
                  </p>
                )}
              </div>

              <Button type="button" className="w-full bg-[#1A3636] hover:bg-[#0D1111] text-white py-6"
                onClick={() => { if (validateStep1()) setStep(2); }} data-testid="step1-next-btn">
                Siguiente
              </Button>
            </div>
          )}

          {/* ===== STEP 2: INE + Datos médicos ===== */}
          {step === 2 && (
            <div className="space-y-4">
              {formData.beneficiary_type !== 'gift' ? (
                <div className="space-y-3">
                  <Label className="flex items-center gap-2">
                    <Camera className="w-4 h-4" />
                    {formData.beneficiary_type === 'minor' ? 'INE del Padre/Madre/Tutor (solo parte frontal)' : 'Identificación Oficial INE (solo parte frontal)'}
                    <span className="text-xs font-normal text-gray-400">(opcional)</span>
                  </Label>

                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-sm text-amber-900 font-medium">¿No tienes tu INE a la mano?</p>
                        <p className="text-xs text-amber-700 mt-1 leading-relaxed">
                          No te preocupes, puedes continuar con tu compra y subir tu INE después desde tu panel de usuario. Tu membresía quedará lista una vez la subas.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <Shield className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-sm text-blue-900 font-medium">Tu identificación está protegida</p>
                        <p className="text-xs text-blue-700 mt-1 leading-relaxed">
                          {formData.beneficiary_type === 'minor' 
                            ? 'Solicitamos la parte frontal del INE del padre, madre o tutor legal para garantizar que, al momento de la atención, el servicio se brinde al menor registrado bajo la autorización de su responsable legal.'
                            : 'Solicitamos únicamente la parte frontal de tu INE con el fin de verificar que, al momento de la atención, el servicio se brinde a la persona que adquirió la membresía. Esto nos permite cuidar tu inversión y evitar usos indebidos de tu servicio.'
                          }
                        </p>
                      </div>
                    </div>
                  </div>

                  <div 
                    onClick={() => fileInputRef.current?.click()}
                    className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
                      inePreview ? 'border-emerald-400 bg-emerald-50' : 'border-gray-300 hover:border-[#C81D25] hover:bg-red-50'
                    }`}
                    data-testid="ine-upload-area"
                  >
                    {inePreview ? (
                      <div className="space-y-3">
                        <img src={inePreview} alt="INE Preview" className="max-h-40 mx-auto rounded-lg shadow" />
                        <p className="text-sm text-emerald-600 font-medium flex items-center justify-center gap-1">
                          <CheckCircle className="w-4 h-4" /> INE cargada correctamente
                        </p>
                        <p className="text-xs text-gray-400">Haz clic para cambiar la imagen</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <Upload className="w-10 h-10 mx-auto text-gray-400" />
                        <p className="text-sm font-medium text-gray-600">Toca aquí para subir la parte frontal de tu INE</p>
                        <p className="text-xs text-gray-400">Foto clara y legible. JPG o PNG (máx. 10MB)</p>
                      </div>
                    )}
                    <input ref={fileInputRef} type="file" accept="image/*" capture="environment" onChange={handleIneChange} className="hidden" data-testid="ine-file-input" />
                  </div>
                </div>
              ) : (
                <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <Gift className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm text-blue-900 font-medium">Membresía como regalo</p>
                      <p className="text-xs text-blue-700 mt-1 leading-relaxed">
                        El beneficiario <strong>{formData.beneficiary_name}</strong> recibirá sus credenciales de acceso y deberá subir la parte frontal de su INE desde su panel de usuario para activar completamente el servicio.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Emergency contact */}
              <div className="border-t pt-4">
                <h3 className="text-sm font-semibold text-[#1A3636] mb-1 flex items-center gap-2">
                  <Phone className="w-4 h-4 text-[#C81D25]" />
                  Contacto de Emergencia
                </h3>
                <p className="text-xs text-gray-400 mb-3">¿A quién debemos notificar si utilizas el servicio?</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Nombre</Label>
                    <Input name="emergency_contact_name" value={formData.emergency_contact_name} onChange={handleChange} placeholder="Nombre completo" data-testid="emergency-contact-name" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Teléfono</Label>
                    <Input name="emergency_contact_phone" value={formData.emergency_contact_phone} onChange={handlePhoneChange} placeholder="55 0000 0000" data-testid="emergency-contact-phone" />
                  </div>
                </div>
              </div>

              {/* Medical info */}
              <div className="border-t pt-4">
                <h3 className="text-sm font-semibold text-[#1A3636] mb-1 flex items-center gap-2">
                  <Heart className="w-4 h-4 text-[#C81D25]" />
                  Información Médica <span className="text-xs font-normal text-gray-400">(opcional)</span>
                </h3>
                <p className="text-xs text-gray-400 mb-3">Estos datos ayudan a nuestros paramédicos a brindarte una atención más rápida y precisa</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs flex items-center gap-1"><Droplets className="w-3 h-3" />Tipo de Sangre</Label>
                    <Select value={formData.blood_type} onValueChange={(v) => setFormData({ ...formData, blood_type: v })}>
                      <SelectTrigger data-testid="blood-type-select"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="O+">O+</SelectItem>
                        <SelectItem value="O-">O-</SelectItem>
                        <SelectItem value="A+">A+</SelectItem>
                        <SelectItem value="A-">A-</SelectItem>
                        <SelectItem value="B+">B+</SelectItem>
                        <SelectItem value="B-">B-</SelectItem>
                        <SelectItem value="AB+">AB+</SelectItem>
                        <SelectItem value="AB-">AB-</SelectItem>
                        <SelectItem value="no_se">No lo sé</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Alergias Conocidas</Label>
                    <Input name="allergies" value={formData.allergies} onChange={handleChange} placeholder="Ej: Penicilina, ninguna" data-testid="allergies-input" />
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" className="flex-1" onClick={() => setStep(1)}>
                  Atrás
                </Button>
                <Button type="button" className="flex-1 bg-[#1A3636] hover:bg-[#0D1111] text-white"
                  onClick={() => { if (validateStep2()) setStep(3); }} data-testid="step2-next-btn">
                  Siguiente
                </Button>
              </div>
            </div>
          )}

          {/* ===== STEP 3: Confirm & Pay ===== */}
          {step === 3 && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                <h3 className="font-semibold text-[#1A3636] text-sm">Resumen de tu registro</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-gray-400">Titular:</span><p className="font-medium">{formData.name}</p></div>
                  <div><span className="text-gray-400">Email:</span><p className="font-medium text-xs break-all">{formData.email}</p></div>
                  <div><span className="text-gray-400">Teléfono:</span><p className="font-medium">{formData.phone}</p></div>
                  <div><span className="text-gray-400">Tipo:</span><p className="font-medium">
                    {formData.beneficiary_type === 'self' ? 'Personal' : formData.beneficiary_type === 'minor' ? 'Menor de edad' : 'Regalo/Tercero'}
                  </p></div>
                  {formData.beneficiary_name && (
                    <div className="col-span-2"><span className="text-gray-400">Beneficiario:</span><p className="font-medium">{formData.beneficiary_name}</p></div>
                  )}
                  {formData.blood_type && formData.blood_type !== 'no_se' && (
                    <div><span className="text-gray-400">Tipo de sangre:</span><p className="font-medium">{formData.blood_type}</p></div>
                  )}
                  {formData.emergency_contact_name && (
                    <div className="col-span-2"><span className="text-gray-400">Contacto emergencia:</span><p className="font-medium">{formData.emergency_contact_name} — {formData.emergency_contact_phone}</p></div>
                  )}
                </div>
                {inePreview ? (
                  <div className="flex items-center gap-2 text-emerald-600 text-sm">
                    <ShieldCheck className="w-4 h-4" /><span>Identificación cargada</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-amber-500 text-sm">
                    <AlertTriangle className="w-4 h-4" /><span>INE pendiente — podrás subirla después desde tu panel</span>
                  </div>
                )}
              </div>

              {/* Price */}
              <div className="bg-gradient-to-r from-[#1A3636] to-[#0D1111] rounded-xl p-5 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/70 text-sm">Membresía Anual SOS FindMed</p>
                    <p className="text-3xl font-bold mt-1">$899 <span className="text-lg font-normal text-white/60">MXN</span></p>
                  </div>
                  <div className="text-right">
                    <p className="text-white/60 text-xs">Protección 24/7</p>
                    <p className="text-white/60 text-xs">Ambulancia de emergencia</p>
                    <p className="text-white/60 text-xs">Vigencia 1 año</p>
                  </div>
                </div>
              </div>

              <label className="flex items-center gap-3 cursor-pointer bg-gray-50 p-3 rounded-lg hover:bg-gray-100 transition-colors">
                <input type="checkbox" checked={autoRenew} onChange={(e) => setAutoRenew(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-[#C81D25] focus:ring-[#C81D25]" data-testid="auto-renew-checkbox" />
                <div>
                  <span className="text-sm font-medium text-[#1A3636]">Activar renovación automática</span>
                  <p className="text-xs text-[#5C6666]">Se renovará automáticamente cada año. Puedes cancelar en cualquier momento.</p>
                </div>
              </label>

              {/* Terms */}
              <div className="space-y-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)}
                    className="mt-1 w-4 h-4 rounded border-gray-300 text-[#C81D25] focus:ring-[#C81D25]" data-testid="accept-terms-checkbox" />
                  <span className="text-sm text-[#5C6666]">
                    Acepto los{' '}<Link to="/terminos" target="_blank" className="text-[#C81D25] font-medium hover:underline">Términos y Condiciones</Link>
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={acceptPrivacy} onChange={(e) => setAcceptPrivacy(e.target.checked)}
                    className="mt-1 w-4 h-4 rounded border-gray-300 text-[#C81D25] focus:ring-[#C81D25]" data-testid="accept-privacy-checkbox" />
                  <span className="text-sm text-[#5C6666]">
                    Acepto el{' '}<Link to="/privacidad" target="_blank" className="text-[#C81D25] font-medium hover:underline">Aviso de Privacidad</Link>
                  </span>
                </label>
              </div>

              {/* Trust badges */}
              <div className="flex items-center justify-center gap-6 py-2 text-gray-400">
                <div className="flex items-center gap-1 text-xs"><Shield className="w-3.5 h-3.5" />Pago seguro</div>
                <div className="flex items-center gap-1 text-xs"><ShieldCheck className="w-3.5 h-3.5" />Datos protegidos</div>
                <div className="flex items-center gap-1 text-xs"><CheckCircle className="w-3.5 h-3.5" />Cobertura 24/7</div>
              </div>

              <div className="flex gap-3">
                <Button type="button" variant="outline" className="flex-1" onClick={() => setStep(2)}>
                  Atrás
                </Button>
                <Button type="button" className="flex-1 bg-[#C81D25] hover:bg-[#A6141B] text-white py-6 text-base font-bold"
                  onClick={handleSubmit} disabled={loading || !acceptTerms || !acceptPrivacy} data-testid="register-submit-button">
                  {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Procesando...</> : 'Pagar $899 MXN'}
                </Button>
              </div>
            </div>
          )}

          <div className="mt-6 text-center">
            <p className="text-[#5C6666]">
              ¿Ya tienes cuenta?{' '}
              <Link to="/login" className="text-[#C81D25] hover:underline font-medium" data-testid="login-link">
                Inicia sesión aquí
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
